// src/config/monetization.ts — MONETIZATION SCAFFOLDING, GUARDED-OFF (M3.5).
// =============================================================================
// "Money plumbing exists, ships dark." (conductor/plan.md M3.5 · aa-bar.md §9)
//
// This module is the SINGLE SOURCE OF TRUTH for the hybrid, subscription-led monetization
// model: a $4.99–6.99/mo "Infinite Topics" subscription (the lead), a rewarded-ad floor, and
// cosmetic-only IAP (decisions.md 2026-06-21). It exists so the rest of the app can ask
// "is X allowed to run yet?" and get a HARD, default-OFF answer — WITHOUT any live ad or
// payment SDK being present.
//
// WHAT THIS MODULE IS:
//   • A typed flag registry + a per-surface interlock gate. Pure data + pure functions.
//   • DEFAULT-OFF, defence-in-depth: a surface is live ONLY if (a) the master switch is on,
//     (b) the surface flag is on, AND (c) every interlock requirement for that surface is
//     proven green. Miss any one → OFF. There is no single boolean that turns money on.
//
// WHAT THIS MODULE IS **NOT** (by design, and enforced by the release firewall):
//   • It imports NO ad SDK (AdMob / Google Mobile Ads), NO payment/billing SDK (RevenueCat /
//     Play Billing / StoreKit / Stripe), and NO consent/CMP SDK. Those need real developer
//     accounts, native (Capacitor) plugins, a published privacy policy + data-safety form, and
//     a working consent flow — none of which can be honestly stubbed in the web bundle. They
//     are integrated ONLY when each interlock below is green (see docs/MONETIZATION.md).
//   • It NEVER gates the core loop. There is intentionally no "energy"/"lives" paywall flag;
//     the only levers are unlock-more-content (subscription), an OPT-IN rewarded ad, and
//     cosmetics. Energy-gating the core verb is a rejected design (decisions.md).
//
// Because nothing wires this into a live surface yet, and because every flag is OFF, this file
// has no runtime effect and (today) is tree-shaken out of the shipped bundle. `tools/firewall.sh`
// guarantees no payment/ad/consent SDK can ride along into dist/ even after wiring begins.
// =============================================================================

/** The three revenue surfaces of the hybrid model. */
export type MonetizationSurface = 'subscription' | 'rewardedAd' | 'cosmeticIap';

/**
 * The compliance interlocks that must ALL be green before a surface may flip ON. Each maps to a
 * concrete, human-verifiable artifact (see docs/MONETIZATION.md "Interlock proof table").
 *
 *   consent      — a working consent / CMP flow (GDPR/ePrivacy + Google UMP + ATT on iOS) that
 *                  records and honors the user's choice BEFORE any tracking/personalized-ad call.
 *   privacyPolicy — a published, linkable privacy policy that discloses data use & the ad/payment
 *                  processors, reachable from the app and the store listing.
 *   dataSafety   — the store data-safety / privacy-nutrition declarations filed and matching the
 *                  SDKs actually shipped (Play Data safety form, App Store privacy labels).
 *   sandboxPurchaseProof — a real sandbox/test transaction completed end-to-end (purchase,
 *                  restore, refund/cancel) on the billing SDK, with evidence captured.
 */
export type Interlock = 'consent' | 'privacyPolicy' | 'dataSafety' | 'sandboxPurchaseProof';

/** Which interlocks each surface requires before it may flip ON. (All are required = AND.) */
export const SURFACE_INTERLOCKS: Readonly<Record<MonetizationSurface, readonly Interlock[]>> = {
  // A subscription processes real money → needs a billing SDK proven in sandbox, a privacy
  // policy, and a filed data-safety declaration. Consent (personalized billing analytics) is
  // covered under consent. Never gates core content — it UNLOCKS more (Infinite Topics).
  subscription: ['privacyPolicy', 'dataSafety', 'sandboxPurchaseProof'],
  // Rewarded ads are the heaviest privacy surface: an ad SDK + (often) personalized ads → consent
  // and data-safety are mandatory; a privacy policy must disclose the ad network. No purchase, so
  // no sandbox-purchase proof. ALWAYS opt-in ("watch an ad for +1 scan"), never forced.
  rewardedAd: ['consent', 'privacyPolicy', 'dataSafety'],
  // Cosmetic IAP is real money but no tracking → billing sandbox proof + privacy policy +
  // data-safety. Cosmetics only; never pay-to-win, never touches the core loop.
  cosmeticIap: ['privacyPolicy', 'dataSafety', 'sandboxPurchaseProof'],
} as const;

/** Per-surface enable flags. ALL DEFAULT FALSE. Flipping one is necessary but NOT sufficient. */
export interface SurfaceFlags {
  subscription: boolean;
  rewardedAd: boolean;
  cosmeticIap: boolean;
}

/** Live status of each compliance interlock. ALL DEFAULT FALSE — nothing is proven until it is. */
export type InterlockStatus = Record<Interlock, boolean>;

/** The full, frozen-by-default monetization configuration. */
export interface MonetizationConfig {
  /**
   * MASTER kill-switch. While false, NO monetization surface can be live regardless of the
   * per-surface flags or interlocks. This is the top-level "ships dark" guarantee.
   */
  enabled: boolean;
  /** Per-surface flags (each ALSO requires its interlocks). */
  surfaces: SurfaceFlags;
  /** Live status of each compliance interlock (the SDK-interlock gate). */
  interlocks: InterlockStatus;
  /** Subscription price band (display/plumbing only; not charged while disabled). */
  subscriptionPriceUsd: { min: number; max: number };
}

/**
 * THE GUARDED-OFF DEFAULT. Everything false. This is what ships at M3.5 ("plumbing present +
 * provably disabled"). Frozen so a stray mutation can't silently turn money on at runtime.
 */
export const DEFAULT_MONETIZATION_CONFIG: Readonly<MonetizationConfig> = Object.freeze({
  enabled: false,
  surfaces: Object.freeze({
    subscription: false,
    rewardedAd: false,
    cosmeticIap: false,
  }),
  interlocks: Object.freeze({
    consent: false,
    privacyPolicy: false,
    dataSafety: false,
    sandboxPurchaseProof: false,
  }),
  subscriptionPriceUsd: Object.freeze({ min: 4.99, max: 6.99 }),
}) as Readonly<MonetizationConfig>;

// The active config starts as the guarded-OFF default and can ONLY be widened by a remote growth-config
// fetched at boot (applyRemoteGrowthConfig). Two independent gates still both apply: a remote payload can
// flip a flag, but isSurfaceLive/isGrowthSdkAllowed re-check the interlocks afterward, and GROWTH_MASTER=off
// on the server returns an all-false payload — so a bad ship is killed by one env var, no store round-trip.
let activeConfig: Readonly<MonetizationConfig> = DEFAULT_MONETIZATION_CONFIG;

/** Resolve the active config (the frozen-OFF default until a remote growth-config is applied). */
export function getMonetizationConfig(): Readonly<MonetizationConfig> {
  return activeConfig;
}

function asBool(v: unknown): boolean {
  return v === true;
}

/**
 * Merge a remote growth-config payload (from GET /growth-config) over the frozen default. FAIL-CLOSED:
 * only keys the payload explicitly sets to `true` can turn on; anything missing/malformed stays OFF, and
 * any throw resets to the guarded default. Pure — it never fetches (the integration layer fetches and
 * passes the parsed JSON in). isSurfaceLive/isGrowthSdkAllowed still re-check interlocks afterward.
 */
export function applyRemoteGrowthConfig(remote: unknown): Readonly<MonetizationConfig> {
  try {
    const r = (remote ?? {}) as Record<string, unknown>;
    const s = (r.surfaces ?? {}) as Record<string, unknown>;
    const i = (r.interlocks ?? {}) as Record<string, unknown>;
    activeConfig = Object.freeze({
      enabled: asBool(r.enabled),
      surfaces: Object.freeze({
        subscription: asBool(s.subscription),
        rewardedAd: asBool(s.rewardedAd),
        cosmeticIap: asBool(s.cosmeticIap),
      }),
      interlocks: Object.freeze({
        consent: asBool(i.consent),
        privacyPolicy: asBool(i.privacyPolicy),
        dataSafety: asBool(i.dataSafety),
        sandboxPurchaseProof: asBool(i.sandboxPurchaseProof),
      }),
      subscriptionPriceUsd: DEFAULT_MONETIZATION_CONFIG.subscriptionPriceUsd,
    }) as Readonly<MonetizationConfig>;
  } catch {
    activeConfig = DEFAULT_MONETIZATION_CONFIG; // fail closed on any malformed payload
  }
  return activeConfig;
}

/** Reset to the guarded-OFF default (tests, or a remote kill that returns nothing). */
export function resetMonetizationConfig(): void {
  activeConfig = DEFAULT_MONETIZATION_CONFIG;
}

/** Master switch helper. */
export function isMonetizationEnabled(cfg: Readonly<MonetizationConfig> = getMonetizationConfig()): boolean {
  return cfg.enabled === true;
}

/** Are ALL interlocks a surface requires currently green? */
export function interlocksGreen(
  surface: MonetizationSurface,
  cfg: Readonly<MonetizationConfig> = getMonetizationConfig(),
): boolean {
  return SURFACE_INTERLOCKS[surface].every((req) => cfg.interlocks[req] === true);
}

/**
 * THE ONE gate the app must call before touching ANY monetization surface. A surface is live only
 * when the master switch is on AND its flag is on AND every interlock it requires is green.
 * Default config → always false. This is the function that keeps the plumbing dark.
 */
export function isSurfaceLive(
  surface: MonetizationSurface,
  cfg: Readonly<MonetizationConfig> = getMonetizationConfig(),
): boolean {
  if (!isMonetizationEnabled(cfg)) return false;
  if (cfg.surfaces[surface] !== true) return false;
  return interlocksGreen(surface, cfg);
}

/** Human-readable reason a surface is NOT live (for the dev overlay / compliance audit). */
export function whySurfaceBlocked(
  surface: MonetizationSurface,
  cfg: Readonly<MonetizationConfig> = getMonetizationConfig(),
): string[] {
  const reasons: string[] = [];
  if (!isMonetizationEnabled(cfg)) reasons.push('master switch (config.enabled) is OFF');
  if (cfg.surfaces[surface] !== true) reasons.push(`surface flag '${surface}' is OFF`);
  for (const req of SURFACE_INTERLOCKS[surface]) {
    if (cfg.interlocks[req] !== true) reasons.push(`interlock '${req}' is not green`);
  }
  return reasons;
}

/** Non-revenue growth SDKs that still carry privacy scope (crash reporting, install attribution). */
export type GrowthSdk = 'crash' | 'attribution';

/** Interlocks each growth SDK requires before it may initialize (ads-equivalent privacy gating). */
export const GROWTH_SDK_INTERLOCKS: Readonly<Record<GrowthSdk, readonly Interlock[]>> = {
  // Crash/diagnostics (e.g. Sentry) can capture device/install ids → consent + data-safety.
  crash: ['consent', 'dataSafety'],
  // Attribution (Play Install Referrer — collects no GAID) → consent + data-safety for the referrer.
  attribution: ['consent', 'dataSafety'],
} as const;

/**
 * Gate for the non-revenue growth SDKs (crash, attribution). Rides the SAME master switch as the money
 * surfaces, so one kill (GROWTH_MASTER=off → all-false remote config) flips the ENTIRE growth stack dark,
 * and still requires consent + a filed data-safety declaration before either SDK may initialize.
 */
export function isGrowthSdkAllowed(
  sdk: GrowthSdk,
  cfg: Readonly<MonetizationConfig> = getMonetizationConfig(),
): boolean {
  if (!isMonetizationEnabled(cfg)) return false;
  return GROWTH_SDK_INTERLOCKS[sdk].every((req) => cfg.interlocks[req] === true);
}

/**
 * A typed registry of the SDKs each surface needs at flip-on time — for documentation and the
 * deploy checklist ONLY. NONE are imported here; integrating any of them is gated on the matching
 * interlocks (docs/MONETIZATION.md). Listing the NAME is safe; importing the SDK is not.
 */
export const PLANNED_SDKS: Readonly<Record<MonetizationSurface, readonly string[]>> = {
  subscription: ['billing SDK (e.g. RevenueCat / Play Billing / StoreKit 2)'],
  rewardedAd: ['ad SDK (e.g. Google Mobile Ads / AdMob)', 'consent/CMP SDK (e.g. Google UMP)'],
  cosmeticIap: ['billing SDK (e.g. RevenueCat / Play Billing / StoreKit 2)'],
} as const;
